import { FilterInputType, FilterInputTemplate, handleAddressChange } from "@/app/home/components/filter/FilterBar";
import { ReportTableConfigs } from "@/app/home/components/report/ReportTable";
import { GlobalUtils } from "@/app/home/utils/global.utils";
import { WaterUserDepositStatus, WaterUserStatus } from "@/core/enums/global.enums";
import { DepositSummaryModel } from "@/core/models/contracts/contract.model";
import { contractStatusPipe } from "@/core/pipes/contract-status.pipe";
import { depositStatusPipe } from "@/core/pipes/deposit-status.pipe";
import { formatCurrencyPipe } from "@/core/pipes/format-currency.pipe";
import { formatDateTimePipe } from "@/core/pipes/format-date.pipe";
import { AddressRepo } from "@/core/repos/address.repo";
import { DatabaseRecord } from "@/core/repos/table.repo";
import { ContractRepo } from "@/core/repos/tables/contract.repo";
import { TollAreaRepo } from "@/core/repos/tables/toll-area.repo";
import container from "@/lib/di/container";
import { Table } from "antd";
import dayjs from "dayjs";

const contractRepo = container.get(ContractRepo);
const tollAreaRepo = container.get(TollAreaRepo);
const addressRepo = container.get(AddressRepo);

export const DEPOSITS_REPORT_CONFIGS: {name: string, props: ReportTableConfigs<DatabaseRecord, DepositSummaryModel[]>} =  {
    name: "Báo cáo Thu tiền cọc",
    props: {
        factoryScopedWards: true,
        columns: [
            { dataIndex: 'code', name: 'Mã HĐ' },
            { dataIndex: 'name', name: 'Đại diện HĐ' },
            { dataIndex: "tollAreaName", name: "Khu vực" },
            { dataIndex: "unitTypeName", name: "Loại hình đơn vị" },
            { dataIndex: 'requiredDepositAmount', name: 'Số tiền phải cọc', pipe: formatCurrencyPipe },
            { dataIndex: 'depositAmount', name: 'Số tiền đã nộp', pipe: formatCurrencyPipe },
            { dataIndex: 'depositStatus', name: 'Trạng thái thu cọc', pipe: depositStatusPipe },
        ],
        filterTemplates:[
            {
                dataIndex: "search",
                label: "Tên/ Mã hợp đồng",
                placeholder: "Nhập Tên/ Mã hợp đồng",
                type: FilterInputType.text,
            },
            {
                dataIndex: "note",
                label: "Ghi chú",
                placeholder: "Nhập ghi chú",
                type: FilterInputType.text,
            },
            {
                dataIndex: "meterCode",
                label: "Mã đồng hồ",
                placeholder: "Nhập mã đồng hồ",
                type: FilterInputType.text,
            },
            {
                dataIndex: "phone",
                label: "Số điện thoại",
                placeholder: "Nhập số điện thoại",
                type: FilterInputType.text,
            },
            {
                dataIndex: "identityNo",
                label: "CCCD",
                placeholder: "Nhập CCCD",
                type: FilterInputType.text,
            },
            {
                dataIndex: "hasMeter",
                label: "Trạng thái lắp đồng hồ",
                placeholder: "Chọn Trạng thái lắp đồng hồ",
                type: FilterInputType.select,
                options: [
                    {
                    name: "Có đồng hồ",
                    value: true,
                    },
                    {
                    name: "Chưa có đồng hồ",
                    value: false,
                    },
                ],
            },
            {
                dataIndex: "status",
                label: "Trạng thái hoạt động",
                placeholder: "Chọn Trạng thái hoạt động",
                type: FilterInputType.select,
                options: [
                    {
                    name: contractStatusPipe(WaterUserStatus.Processing),
                    value: WaterUserStatus.Processing,
                    },
                    {
                    name: contractStatusPipe(WaterUserStatus.Active),
                    value: WaterUserStatus.Active,
                    },
                    {
                    name: contractStatusPipe(WaterUserStatus.TempDisabled),
                    value: WaterUserStatus.TempDisabled,
                    },
                    {
                    name: contractStatusPipe(WaterUserStatus.Disabled),
                    value: WaterUserStatus.Disabled,
                    },
                ],
            },
            {
                dataIndex: "tollAreaId",
                label: "Khu vực",
                placeholder: "Chọn Khu vực",
                type: FilterInputType.select,
                options: [],
            },
            {
                dataIndex: "provinceId",
                label: "Tỉnh/ TP",
                placeholder: "Chọn Tỉnh/ TP",
                type: FilterInputType.select,
                options: [],
            },
            {
                dataIndex: "districtId",
                label: "Quận/ Huyện",
                placeholder: "Chọn Quận/ Huyện",
                type: FilterInputType.select,
                options: [],
            },
            {
                dataIndex: "wardId",
                label: "Xã/ Phường",
                placeholder: "Chọn Xã/ Phường",
                type: FilterInputType.select,
                options: [],
            },
            {
                dataIndex: "depositStatus",
                label: "Trạng thái thu cọc",
                placeholder: "Chọn trạng thái thu cọc",
                type: FilterInputType.select,
                options: Object.keys(WaterUserDepositStatus).map((key) => ({name: depositStatusPipe(key), value: key})),
            },
        ],
        handleExport: async (filterMap) => {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const query: any = {};
            filterMap.forEach(filter => {
                if (filter.value) {
                    query[filter.key] = filter.value
                }
            })
            const blobData = await contractRepo.exportExcel(query);
            if (blobData) {
               GlobalUtils.downloadXLSX(blobData, `DanhSachHopDongNuoc-${dayjs().format("DDMMYYYY-hhmm")}`)
            };
        },
        init: async (filterTemplates) => {
            const tollAreas = (await tollAreaRepo.getAll()).map((area) => ({name: area.name, value: area.id}));
            const provinces = (await addressRepo.getAllProvinces()).map(data => ({ name: data[1].name, value: data[0] }));
            const templates: FilterInputTemplate[] = [...filterTemplates];
            const tollAreaTemplate = templates.find(template => template.dataIndex === "tollAreaId");
            if (tollAreaTemplate) {
                tollAreaTemplate.options = tollAreas;
            }
            const provinceTemplate = templates.find(template => template.dataIndex === "provinceId");
            if (provinceTemplate) {
                provinceTemplate.options = provinces;
            }

            return {
                filterTemplates: templates
            }
        },
        onFilterChanged: async (map, changes, filterTemplates) => {
            const templates = await handleAddressChange(map, changes, filterTemplates);
            return { filterTemplates: templates, filterMap: templates ? map : undefined };
        },
        repository: contractRepo,
        getSummary: async (filterMap) => {
            const query: any = {};
            filterMap.forEach(filter => {
                if (filter.value) {
                    query[filter.key] = filter.value
                }
            })
            return await contractRepo.getSummary(query);
        },
        renderSummary: (summary: DepositSummaryModel[]) => {
            if (summary.length === 0) {
                return <div className="px-4 flex flex-col justify-center h-[60px] text-center font-bold">Không có dữ liệu</div>
            }
            const total: DepositSummaryModel = summary.reduce((prev, curr) => ({
                depositAmount: prev.depositAmount + curr.depositAmount,
                requiredDepositAmount: prev.requiredDepositAmount + curr.requiredDepositAmount,
                statusAmount: prev.statusAmount + curr.statusAmount,
                status: WaterUserDepositStatus.All 
            }))
            let summaryData = [...summary]
            if (summary.length > 1) {
                summaryData = [total, ...summary];
            }
            const getColor = (status: WaterUserDepositStatus): string => {
                switch (status) {
                    case WaterUserDepositStatus.All: return "black";
                    case WaterUserDepositStatus.FullPaid: return "var(--success-button)";
                    case WaterUserDepositStatus.HalfPaid: return "var(--warning-button)";
                    case WaterUserDepositStatus.No: return "var(--danger-color)";
                }
            }
            return <>
                {
                   summaryData.map((row, index) => (
                    <Table.Summary.Row key={row.status} style={{color: getColor(row.status)}}>
                        <Table.Summary.Cell index={0}>
                            <div className="px-4 flex flex-col justify-center h-[60px] text-center font-bold">Tổng</div>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={1}>
                            <div className="px-4 flex flex-col justify-center h-[60px] font-bold">{row.statusAmount}</div>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={2}/>
                        <Table.Summary.Cell index={3}/>
                        <Table.Summary.Cell index={4}/>
                        <Table.Summary.Cell index={5}>
                            <div className="px-4 flex flex-col justify-center h-[60px] font-bold">{formatCurrencyPipe(row.requiredDepositAmount)}</div>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={6}>
                            <div className="px-4 flex flex-col justify-center h-[60px] font-bold">{formatCurrencyPipe(row.depositAmount)}</div>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={7}>
                            <div className="px-4 flex flex-col justify-center h-[60px] font-bold">{depositStatusPipe(row.status)}</div>
                        </Table.Summary.Cell>
                    </Table.Summary.Row>
                   )) 
                }   
            </>
        }
    }
}